import * as React from "react";
import omit from "lodash/omit";

import Debug from "@/react/debug";
import { errorNotification, createNotifier } from "@/react/notifications";
import { ERRORS, KEY } from "@/react/components/table/useTableMutations";
import { handleMutationResponse as createMutationResponseHandler } from "@/react/services/api/handleMutationResponse";

import {
  useCreateAgentQueueAssignmentMutation,
  useDeleteAgentQueueAssignmentMutation,
  useUpdateAgentQueueAssignmentMutation,
} from "./api";

const ENTITY_NAME = "agent assignment";
const DELETE_NOTIFIER = createNotifier("delete", ENTITY_NAME);
const UPDATE_NOTIFIER = createNotifier("update", ENTITY_NAME);
const CREATE_NOTIFIER = createNotifier("create", ENTITY_NAME);
const NAME_FIELDS_TO_SKIP = Object.freeze(["agentName", "queueName"]);
const FIELDS_TO_SKIP_ON_DELETE = Object.freeze(
  ["isEnabled", "skillLevel"].concat(NAME_FIELDS_TO_SKIP)
);

const useAgentAssignmentsMutations = (table, tenantName, mutations) => {
  const {
    rowsToDelete,
    setRowsToDelete,
    rowsToUpdate,
    setRowsToUpdate,
    rowsToCreate,
    setRowsToCreate,
    setData,
    resetData,
  } = mutations;
  const [deleteAssignments, { isLoading: isDeleting }] =
    useDeleteAgentQueueAssignmentMutation();
  const [updateAssignment, { isLoading: isUpdating }] =
    useUpdateAgentQueueAssignmentMutation();
  const [createAssignment, { isLoading: isCreating }] =
    useCreateAgentQueueAssignmentMutation();

  const onDeleteResponseHandler = React.useMemo(
    () =>
      createMutationResponseHandler({
        rows: rowsToDelete,
        setRows: setRowsToDelete,
        setData,
        idKey: KEY,
        notifyContent: DELETE_NOTIFIER,
        isVoidResponse: true,
      }),
    [rowsToDelete, setRowsToDelete, setData]
  );

  const onUpdateResponseHandler = React.useMemo(
    () =>
      createMutationResponseHandler({
        rows: rowsToUpdate,
        setRows: setRowsToUpdate,
        setData,
        idKey: KEY,
        notifyContent: UPDATE_NOTIFIER,
      }),
    [rowsToUpdate, setRowsToUpdate, setData]
  );
  const onCreateResponseHandler = React.useMemo(
    () =>
      createMutationResponseHandler({
        rows: rowsToCreate,
        setRows: setRowsToCreate,
        setData,
        idKey: KEY,
        notifyContent: CREATE_NOTIFIER,
      }),
    [rowsToCreate, setRowsToCreate, setData]
  );

  const onSave = React.useCallback(() => {
    const promises = [];

    if (rowsToDelete.size > 0) {
      // For this endpoint, the payload does not just need to contain the IDs,
      // but instead they should contain the entire entity (sans any errors and
      // _key).
      const payload = Array.from(rowsToDelete.values()).map((value) =>
        omit(value, [ERRORS, KEY, ...FIELDS_TO_SKIP_ON_DELETE])
      );

      Debug.log("[useAgentAssignmentsMutations] Delete payload", payload);

      promises.push(
        deleteAssignments({ tenantName, payload })
          .unwrap()
          .then(onDeleteResponseHandler)
      );
    }

    if (rowsToUpdate.size > 0) {
      const payload = Array.from(rowsToUpdate.entries()).map(
        ([id, changes]) => {
          const originalRowData = table.getRow(id).original;
          return omit(
            {
              ...originalRowData,
              ...changes,
            },
            [ERRORS, KEY, ...NAME_FIELDS_TO_SKIP]
          );
        }
      );

      promises.push(
        updateAssignment({ tenantName, payload })
          .unwrap()
          .then(onUpdateResponseHandler)
      );
    }

    if (rowsToCreate.size > 0) {
      const payload = Array.from(rowsToCreate.entries()).map(
        ([id, changes]) => {
          const originalRowData = table.getRow(id).original;
          return omit(
            {
              ...originalRowData,
              ...changes,
            },
            [ERRORS, KEY, ...NAME_FIELDS_TO_SKIP]
          );
        }
      );
      promises.push(
        createAssignment({ tenantName, payload })
          .unwrap()
          .then(onCreateResponseHandler)
      );
    }

    Promise.all(promises).catch((error) => {
      Debug.error("Failed to manage agent assignments:", error);
      errorNotification(error);
    });
  }, [
    rowsToDelete,
    rowsToUpdate,
    rowsToCreate,
    deleteAssignments,
    updateAssignment,
    createAssignment,
    tenantName,
    onDeleteResponseHandler,
    onUpdateResponseHandler,
    onCreateResponseHandler,
  ]);

  return {
    onSave,
    onCancel: resetData,
    isMutationOngoing: isDeleting || isUpdating || isCreating,
    isDirty:
      rowsToDelete.size > 0 || rowsToUpdate.size > 0 || rowsToCreate.size > 0,
  };
};

export default useAgentAssignmentsMutations;
